import type {
  GalleryModalOpenProps,
  GalleryModalZoom,
  UseGalleryModalProps,
  UseGalleryModalZoomProps,
} from "./gallery-modal.type";

import { useHotkey } from "@tanstack/react-hotkeys";
import { useState } from "react";

import { useElementSize } from "@/utilities/element";

import { GALLERY_MODAL_ZOOM } from "./gallery-modal.constant";
import {
  getContainerScroll,
  getGalleryModalTitle,
  getSlideStyle,
} from "./gallery-modal.helper";

export const useGalleryModalOpen = ({
  defaultOpen = false,
  onClose,
}: GalleryModalOpenProps) => {
  const [open, setOpen] = useState(defaultOpen);

  const closeModal = () => {
    setOpen(false);
    setTimeout(() => onClose?.(), 500); // wait for css transition
  };

  return {
    open,
    closeModal,
  };
};

export const useGalleryModal = ({
  items,
  defaultCurrentSlide = 0,
}: UseGalleryModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(defaultCurrentSlide);

  const {
    containerRef,
    currentSlideZoom,
    currentSlideStyle,
    resetSlideZoom,
    zoomIn,
    zoomOut,
  } = useGalleryModalZoom({ currentIndex });

  const currentSlide = items[currentIndex];
  const title = getGalleryModalTitle(currentSlide);
  const progress = `${currentIndex + 1} / ${items.length}`;

  const goToPrevious = () => {
    resetSlideZoom(currentIndex);
    const newIndex = currentIndex - 1;
    setCurrentIndex(newIndex < 0 ? items.length - 1 : newIndex);
  };

  const goToNext = () => {
    resetSlideZoom(currentIndex);
    const newIndex = currentIndex + 1;
    setCurrentIndex(newIndex === items.length ? 0 : newIndex);
  };

  useHotkey("ArrowLeft", goToPrevious);
  useHotkey("ArrowRight", goToNext);

  return {
    open,
    containerRef,
    currentSlide: {
      ...currentSlide,
      zoom: currentSlideZoom,
      style: currentSlideStyle,
    },
    title,
    progress,
    goToPrevious,
    goToNext,
    zoomIn,
    zoomOut,
  };
};

export const useGalleryModalContainer = () => {
  const { ref, size } = useElementSize();

  const width = size?.width ?? 0;
  const height = size?.height ?? 0;

  return {
    containerRef: ref,
    containerSize: { width, height },
  };
};

export const useGalleryModalZoom = ({
  currentIndex,
}: UseGalleryModalZoomProps) => {
  const { containerRef, containerSize } = useGalleryModalContainer();

  const [zoom, setZoom] = useState<GalleryModalZoom>({});

  const getSlideZoom = (slideIndex: number) => {
    return zoom[slideIndex] ?? GALLERY_MODAL_ZOOM.INITIAL;
  };

  const setSlideZoom = (slideIndex: number, zoomLevel: number) => {
    setZoom((zoom) => ({ ...zoom, [slideIndex]: zoomLevel }));
  };

  const resetSlideZoom = (slideIndex: number) => {
    setSlideZoom(slideIndex, GALLERY_MODAL_ZOOM.INITIAL);
  };

  const zoomIn = () => {
    const newZoom = getSlideZoom(currentIndex) + GALLERY_MODAL_ZOOM.INCREMENT;
    updateZoom(newZoom);
  };

  const zoomOut = () => {
    const newZoom = getSlideZoom(currentIndex) - GALLERY_MODAL_ZOOM.INCREMENT;
    updateZoom(newZoom);
  };

  const updateZoom = (newZoom: number) => {
    setSlideZoom(currentIndex, newZoom);

    if (newZoom <= 1) return;

    // Update scroll twice to avoid ui glitch
    // - First update to apply scroll right after zoom change and avoid delay
    // - Second update when first update did happened too early and was not applied
    updateScroll(newZoom);
    setTimeout(() => updateScroll(newZoom), 1);
  };

  const updateScroll = (newZoom: number) => {
    const container = containerRef.current;

    if (!container) return;

    const scroll = getContainerScroll({ zoom: newZoom, containerSize });
    container.scrollTo(scroll);
  };

  const currentZoom = getSlideZoom(currentIndex);
  const currentStyle = getSlideStyle({ zoom: currentZoom, containerSize });

  return {
    containerRef,
    currentSlideZoom: currentZoom,
    currentSlideStyle: currentStyle,
    resetSlideZoom,
    zoomIn,
    zoomOut,
  };
};
